import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import httpErrors from 'http-errors';
import { lastValueFrom } from 'rxjs';
import urlJoin from 'url-join';

export class AbstractApiService {
  constructor(protected readonly serviceBaseUrl: string, protected readonly http: HttpClient) {}

  async get<T = unknown>(
    url: string,
    httpOptions: Record<string, unknown> = {},
  ): Promise<HttpResponse<T>> {
    return lastValueFrom(
      this.http.get<T>(urlJoin(this.serviceBaseUrl, url), {
        ...httpOptions,
        observe: 'response',
      }),
    )
      .then(response => {
        if (response && response.status !== 200) {
          throw httpErrors(response.status, response.statusText);
        }

        return response;
      })
      .catch((...args) => this.parseError<T>(...args));
  }

  async post<T = unknown>(
    url: string,
    body: unknown | null = null,
    httpOptions: Record<string, unknown> = {},
  ): Promise<HttpResponse<T>> {
    return lastValueFrom(
      this.http.post<T>(urlJoin(this.serviceBaseUrl, url), body, {
        ...httpOptions,
        observe: 'response',
      }),
    )
      .then(response => {
        if (response && ![200, 201].includes(response.status)) {
          throw httpErrors(response.status, response.statusText);
        }

        return response;
      })
      .catch((...args) => this.parseError<T>(...args));
  }

  async put<T = unknown>(
    url: string,
    body: unknown | null = null,
    httpOptions: Record<string, unknown> = {},
  ): Promise<HttpResponse<T>> {
    return lastValueFrom(
      this.http.put<T>(urlJoin(this.serviceBaseUrl, url), body, {
        ...httpOptions,
        observe: 'response',
      }),
    )
      .then(response => {
        if (response && ![200, 201].includes(response.status)) {
          throw httpErrors(response.status, response.statusText);
        }

        return response;
      })
      .catch((...args) => this.parseError<T>(...args));
  }

  async patch<T = unknown>(
    url: string,
    body: unknown | null = null,
    httpOptions: Record<string, unknown> = {},
  ): Promise<HttpResponse<T>> {
    return lastValueFrom(
      this.http.patch<T>(urlJoin(this.serviceBaseUrl, url), body, {
        ...httpOptions,
        observe: 'response',
      }),
    )
      .then(response => {
        if (response && ![200, 201].includes(response.status)) {
          throw httpErrors(response.status, response.statusText);
        }

        return response;
      })
      .catch((...args) => this.parseError<T>(...args));
  }

  async delete<T = unknown>(
    url: string,
    httpOptions: Record<string, unknown> = {},
  ): Promise<HttpResponse<T>> {
    return lastValueFrom(
      this.http.delete<T>(urlJoin(this.serviceBaseUrl, url), {
        ...httpOptions,
        observe: 'response',
      }),
    )
      .then(response => {
        if (response && ![202, 204].includes(response.status)) {
          throw httpErrors(response.status, response.statusText);
        }

        return response;
      })
      .catch((...args) => this.parseError<T>(...args));
  }

  private parseError<T>(error: HttpErrorResponse): HttpResponse<T> {
    let cur = error;
    while (!!cur.error && typeof cur.error === 'object') {
      cur = cur.error;
    }
    throw cur;
  }
}
